import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { DirectedCastEntry, DirectedShot } from 'fym-shared/src/music24/directedVideoApi';
import type { MusicVideoEffect } from 'fym-shared/src/music24/musicVideoEffects';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { effectKindMessages } from '../../musicVideoEditor/messages/effectMessages';
import { formatTime } from '../../musicVideoEditor/timeline/timelineGeometry';
import { scriptMessages as messages } from '../messages/scriptMessages';
import { storyMessages } from '../messages/storyMessages';
import { useDirectedCastNames } from '../useDirectedCastNames';
import type { BeatSection } from './director/choiceRules';
import { SECTION_DOT } from './director/sectionDots';
import { EffectChip } from './script/EffectChip';
import { EditIcon, RemoveIcon, SparkleIcon } from './script/ShotIcons';
import { ShotThumb } from './script/ShotThumb';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the script step; each part uses its own slice of it */
import styles from './script/Script.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import steps from './DirectorSteps.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import shell from '../DirectedVideo.module.scss';
/* eslint-enable css-modules/no-unused-class */

export type ShotRowMode = 'view' | 'editing' | 'removing' | 'rewriting';

interface ShotRowProps {
  shot: DirectedShot;
  number: number;
  clipNumber: number;
  /** The first shot of its clip: a new AI shot. Else it uses a part of the clip. */
  isClipStart: boolean;
  section: BeatSection | null;
  cast: readonly DirectedCastEntry[];
  effects: readonly MusicVideoEffect[];
  /** A still of the made clip of this shot, else null (a placeholder tile). */
  stillUrl: string | null;
  mode: ShotRowMode;
  isEdited: boolean;
  /** The edit fields (mode `editing`). */
  editor?: React.ReactNode;
  /** The note of the rewrite in progress (mode `rewriting`). */
  rewriteNote?: string;
  /** The two lines of the remove question (mode `removing`). */
  removeLines?: [string, string];
  disabled: boolean;
  canRewrite: boolean;
  canRemove: boolean;
  onEdit: () => void;
  onRewrite: () => void;
  onRemove: () => void;
  onKeep: () => void;
  onConfirmRemove: () => void;
}

/**
 * One shot of the script (design e1, e2): the number, the time and the section, the clip it plays
 * from ("Clip 3 · 5.0 s in"), what happens and the camera, the cast and the effects, and Edit,
 * Rewrite, Remove. A remove asks in the row; a rewrite shimmers until the director is done.
 */
const ShotRowComponent: React.FC<ShotRowProps> = ({
  shot,
  number,
  clipNumber,
  isClipStart,
  section,
  cast,
  effects,
  stillUrl,
  mode,
  isEdited,
  editor,
  rewriteNote,
  removeLines,
  disabled,
  canRewrite,
  canRemove,
  onEdit,
  onRewrite,
  onRemove,
  onKeep,
  onConfirmRemove,
}) => {
  const intl = useIntl();
  const { named } = useDirectedCastNames();
  const shotCast = cast.filter((entry) => shot.castIds.includes(entry.id));
  const clipLabel =
    isClipStart || shot.clipInSec === 0
      ? intl.formatMessage(messages.clip, { number: clipNumber })
      : intl.formatMessage(messages.clipIn, { number: clipNumber, seconds: shot.clipInSec.toFixed(1) });
  let cameraLine = shot.camera;
  if (!isClipStart) {
    cameraLine =
      shot.camera === ''
        ? intl.formatMessage(messages.noNewShotNoCamera, { number: clipNumber })
        : intl.formatMessage(messages.noNewShot, { camera: shot.camera, number: clipNumber });
  }

  const times = (
    <div className={styles.times}>
      <span className={styles.time}>
        {formatTime(shot.startSec)}–{formatTime(shot.endSec)}
      </span>
      {section !== null && (
        <span className={styles.section}>
          <i className={clsx(steps.dot, SECTION_DOT[section.tone])} aria-hidden />
          {section.name}
        </span>
      )}
      {isEdited && <span className={steps.edited}>{intl.formatMessage(storyMessages.edited)}</span>}
    </div>
  );

  if (mode === 'rewriting') {
    return (
      <div className={styles.row} data-testid={`shot-row-${shot.id}`} role="status">
        <div className={shell.shimmer} />
        <span className={styles.number}>{number}</span>
        {times}
        <div className={clsx(styles.thumb, styles.thumbWorking)} />
        <p className={clsx(styles.scene, styles.sceneMuted)}>
          {rewriteNote === undefined || rewriteNote.trim() === ''
            ? intl.formatMessage(messages.rewritingNoNote)
            : intl.formatMessage(messages.rewriting, { note: rewriteNote.trim() })}
        </p>
        <div />
        <div className={styles.actions}>
          <span className={styles.spin} aria-hidden />
        </div>
      </div>
    );
  }

  const thumb = <ShotThumb number={number} clipLabel={clipLabel} camera={shot.camera} stillUrl={stillUrl} />;

  if (mode === 'editing') {
    return (
      <div className={clsx(styles.row, styles.rowOn)} data-testid={`shot-row-${shot.id}`}>
        <span className={clsx(styles.number, styles.numberOn)}>{number}</span>
        {times}
        {thumb}
        {editor}
        <div className={styles.actions}>
          <button
            type="button"
            className={clsx(styles.iconButton, styles.iconDanger)}
            aria-label={intl.formatMessage(messages.removeShotAria, { number })}
            title={intl.formatMessage(messages.remove)}
            disabled={disabled || !canRemove}
            onClick={onRemove}
          >
            <RemoveIcon />
          </button>
        </div>
      </div>
    );
  }

  if (mode === 'removing' && removeLines !== undefined) {
    return (
      <div className={clsx(styles.row, styles.rowDanger)} data-testid={`shot-row-${shot.id}`}>
        <span className={styles.number}>{number}</span>
        {times}
        {thumb}
        <div>
          <p className={styles.scene}>{removeLines[0]}</p>
          <p className={styles.cameraLine}>{removeLines[1]}</p>
        </div>
        <div />
        <div className={styles.actions}>
          <SMLButton className={steps.small} onClick={onKeep}>
            {intl.formatMessage(messages.keep)}
          </SMLButton>
          <SMLButton className={clsx(steps.small, styles.iconDanger)} disabled={disabled} onClick={onConfirmRemove}>
            {intl.formatMessage(messages.remove)}
          </SMLButton>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.row} data-testid={`shot-row-${shot.id}`}>
      <span className={styles.number}>{number}</span>
      {times}
      {thumb}
      <div>
        <p className={styles.scene}>{named(shot.scene)}</p>
        {cameraLine !== '' && <p className={styles.cameraLine}>{cameraLine}</p>}
      </div>
      <div className={styles.facts}>
        {shotCast.length > 0 && (
          <div className={styles.castChips}>
            {shotCast.map((entry) => (
              <span key={entry.id} className={styles.castChip}>
                {entry.url === '' ? <i aria-hidden /> : <img src={entry.url} alt="" />}
                {entry.label}
              </span>
            ))}
          </div>
        )}
        {effects.length > 0 && (
          <div className={styles.fxChips}>
            {effects.map((effect) => (
              <EffectChip key={effect.id} effect={effect} label={intl.formatMessage(effectKindMessages[effect.kind])} />
            ))}
          </div>
        )}
      </div>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.iconButton}
          aria-label={intl.formatMessage(messages.editShot, { number })}
          title={intl.formatMessage(messages.editShot, { number })}
          disabled={disabled}
          onClick={onEdit}
        >
          <EditIcon />
        </button>
        <button
          type="button"
          className={styles.iconButton}
          aria-label={intl.formatMessage(messages.rewriteShotAria, { number })}
          title={intl.formatMessage(messages.rewriteShot)}
          disabled={disabled || !canRewrite}
          onClick={onRewrite}
        >
          <SparkleIcon />
        </button>
        <button
          type="button"
          className={clsx(styles.iconButton, styles.iconDanger)}
          aria-label={intl.formatMessage(messages.removeShotAria, { number })}
          title={intl.formatMessage(messages.remove)}
          disabled={disabled || !canRemove}
          onClick={onRemove}
        >
          <RemoveIcon />
        </button>
      </div>
    </div>
  );
};

export const ShotRow = TypedMemo(ShotRowComponent);
